import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, collectionGroup, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../auth/AuthProvider';

/**
 * AppSetup:
 * - Ensures user is signed in
 * - Resolves org membership from:
 *    1) custom claims (orgId)
 *    2) /userRoles/{uid} (orgId or orgIds array)
 * - If multiple orgs => /select-org
 * - If single org => persist localStorage.orgId and go to /cashup
 * - No more /dashboard in this project.
 */
export default function AppSetup() {
  const { user, loading, refreshClaims } = useAuth();
  const nav = useNavigate();
  const [msg, setMsg] = useState('Checking access…');
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav('/login', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setErr(null);
        setMsg('Refreshing permissions…');
        const claims = await refreshClaims();

        // Super goes straight to /super
        const isSuper = claims?.superAdmin === true || claims?.superadmin === true;
        if (isSuper) {
          if (!cancelled) nav('/super', { replace: true });
          return;
        }

        // 1) Prefer orgId from custom claims (single-org fast path)
        let orgId = claims?.orgId ? String(claims.orgId) : null;

        // 2) Fallback to /userRoles/{uid}
        let orgIds = [];
        if (!orgId) {
          setMsg('Loading organization…');
          const snap = await getDoc(doc(db, 'userRoles', user.uid));
          if (snap.exists()) {
            const d = snap.data() || {};
            if (Array.isArray(d.orgIds)) {
              orgIds = d.orgIds.filter(Boolean).map(String);
            }
            if (!orgIds.length && d?.orgId) {
              orgId = String(d.orgId);
            }
          }
        }

        // 3) Fallback to org membership docs by email (supports invited users before claims/userRoles exist)
        if (!orgId && !orgIds.length && user?.email) {
          setMsg('Loading organization membership…');
          const email = String(user.email).trim().toLowerCase();
          const memberSnap = await getDocs(
            query(collectionGroup(db, 'members'), where('email', '==', email), limit(10))
          );
          const found = [];
          memberSnap.forEach((d) => {
            const segs = d.ref.path.split('/');
            const idx = segs.index('orgs');
            const foundOrgId = idx >= 0 ? segs[idx + 1] : '';
            if (foundOrgId) found.push(foundOrgId);
          });
          orgIds = Array.from(new Set(found.filter(Boolean)));
          if (orgIds.length === 1) orgId = orgIds[0];
        }

        // 4) Last fallback to legacy managers by email (older Neo access model)
        if (!orgId && !orgIds.length && user?.email) {
          setMsg('Loading legacy access…');
          const mgrSnap = await getDocs(
            query(collection(db, 'managers'), where('email', '==', String(user.email).trim().toLowerCase()), limit(10))
          );
          const found = [];
          mgrSnap.forEach((d) => {
            const data = d.data() || {};
            if (data.orgId) found.push(String(data.orgId));
          });
          orgIds = Array.from(new Set(found.filter(Boolean)));
          if (orgIds.length === 1) orgId = orgIds[0];
        }

        // If claims had orgId, still allow userRoles orgIds override (multi-org)
        if (orgId && !orgIds.length) {
          // no-op
        } else if (!orgId && orgIds.length === 1) {
          orgId = orgIds[0];
        }

        if (!orgId && orgIds.length > 1) {
          // Persist the list for SelectOrg to use (so we don't re-query if not needed)
          sessionStorage.setItem('orgIds', JSON.stringify(orgIds));
          if (!cancelled) nav('/select-org', { replace: true });
          return;
        }

        if (!orgId) {
          setErr('No organization found for this user. Ask a super admin to assign you to an org.');
          return;
        }

        // Persist for tenant helpers
        localStorage.setItem('orgId', orgId);

        if (!cancelled) nav('/cashup', { replace: true });
      } catch (e) {
        if (!cancelled) setErr(e?.message || String(e));
      }
    })();

    return () => { cancelled = true; };
  }, [user, loading, refreshClaims, nav]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0f19] text-red-400">
        <div className="max-w-lg p-6 bg-[#121a26] rounded-xl shadow">
          <h1 className="text-xl font-semibold mb-2">Access problem</h1>
          <p className="mb-4">{err}</p>
          <button
            className="px-4 py-2 rounded bg-cyan-600 text-white"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0f19] text-white">
      <div className="text-center">
        <div className="animate-pulse text-cyan-400 mb-2">• • •</div>
        <div>{msg}</div>
      </div>
    </div>
  );
}
